import { useState, useCallback } from 'react'
import useInnovaAxios from './useInnovaAxios'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'

function useAlertGroups() {
  const [isLoading, setIsLoading] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [alertGroups, setAlertGroups] = useState([])

  const getAlertGroupsRequest = useInnovaAxios({ url: '/alertGroup/getGroups' })
  const deleteAlertGroupRequest = useInnovaAxios({ url: '/alertGroup/deleteGroup' })
  const addAlertGroupRequest = useInnovaAxios({ url: '/alertGroup/addGroup' })
  const updateAlertGroupRequest = useInnovaAxios({ url: '/alertGroup/updateGroup' })

  /* ============================================================
     Fetch Alert Groups
     ============================================================ */

  const fetchAlertGroups = useCallback(
    async (organization) => {
      setIsLoading(true)

      try {
        const response = await getAlertGroupsRequest({ organization })

        if (response?.error) {
          return failure(extractErrorMessage(response.error, 'Failed to fetch alert groups'))
        }

        const alertGroups = Array.isArray(response?.data)
          ? [...response.data].sort((a, b) => (a.groupName ?? '').localeCompare(b.groupName ?? ''))
          : []

        setAlertGroups(alertGroups)

        return success('Alert groups fetched successfully', alertGroups)
      } finally {
        setIsLoading(false)
      }
    },
    [getAlertGroupsRequest],
  )

  /* ============================================================
       Update Alert Group
       ============================================================ */

  const updateAlertGroup = useCallback(
    async (form) => {
      if (!form?.uid) return failure('Invalid alert group id')
      if (!form?.groupName) return failure('Invalid group data')
      if (isUpdating) return failure('Update already in progress')

      setIsUpdating(true)
      try {
        const payload = { ...form }

        const res = await updateAlertGroupRequest({
          alertGroup: JSON.stringify(payload),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update alert group'))
        }

        return success('Alert group updated')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateAlertGroupRequest, isUpdating],
  )

  /* ============================================================
       Delete Alert Group
       ============================================================ */

  const deleteAlertGroup = useCallback(
    async (uid) => {
      if (!uid) return failure('Invalid alert group id')
      if (isDeleting) return failure('Delete already in progress')

      setIsDeleting(true)
      try {
        const res = await deleteAlertGroupRequest({ uid })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Delete failed'))
        }

        return success('Alert group deleted')
      } finally {
        setIsDeleting(false)
      }
    },
    [deleteAlertGroupRequest, isDeleting],
  )

  /* ============================================================
       Add Alert Group
       ============================================================ */

  const addAlertGroup = useCallback(
    async (form) => {
      if (isAdding) return failure('Add already in progress')
      form.uid = 0

      setIsAdding(true)
      try {
        const res = await addAlertGroupRequest({
          alertGroup: JSON.stringify(form),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to create alert group'))
        }

        return success('Alert group created', res?.data)
      } finally {
        setIsAdding(false)
      }
    },
    [addAlertGroupRequest, isAdding],
  )

  return {
    alertGroups,

    isLoading,
    isAdding,
    isDeleting,
    isUpdating,

    fetchAlertGroups,
    addAlertGroup,
    updateAlertGroup,
    deleteAlertGroup,
  }
}

export default useAlertGroups
